import {observable, action, when, computed, reaction} from 'mobx';
import CoreStore from 'stores/core.store';
import BlogArticle from 'models/BlogArticle';
import BlogService from 'services/blog.service';
import { KENTICO_TAXONOMY } from 'common/consts/kentico';
import {DESKTOP_ARTICLES_PER_PAGE, MOBILE_ARTICLES_PER_PAGE} from "common/consts/blog";
import { slashesToDashes } from "common/methods/string";

class BlogStore {
	@observable loadedArticles: Map<string, any> = new Map();

	@observable page = 1;

	@observable filter: string[] = [];

	@observable articleListLoaded = false;

	@observable articleList: any[] = [];

	@observable blogSections: Map<string, string> = new Map(); // section_taxonomy_codename -> section translated name

	@observable isArticleLoading = false;

	@observable currentArticle: any | null = null;

	@observable articlesPerPage = DESKTOP_ARTICLES_PER_PAGE;

	constructor(private coreStore: CoreStore) {
		when(() => coreStore.initialCoreDataFetchDone)
			.then(this.translateBlogSections);
		this.articlesPerPage = coreStore.windowStore.mediaSmallDesktop ? DESKTOP_ARTICLES_PER_PAGE : MOBILE_ARTICLES_PER_PAGE;
		reaction(() => coreStore.windowStore.mediaSmallDesktop, (isDesktop) => {
			const pagesNormal = this.page / this.pagesCount;
			this.articlesPerPage = isDesktop ? DESKTOP_ARTICLES_PER_PAGE : MOBILE_ARTICLES_PER_PAGE;
			this.page = Math.floor(this.pagesCount * pagesNormal);
		});
	}

	@computed
	get filteredArticleList() {
		if (this.filter.length === 0) {
			return this.articleList;
		}
		return this.articleList.filter((article) => article.categories
			.some((category) => this.filter.some((codename) => codename === category.codename)));
	}

	@computed
	get pagedArticleList() {
		return this.filteredArticleList.slice((this.page - 1) * this.articlesPerPage, (this.page) * this.articlesPerPage);
	}

	@computed
	get pagesCount() {
		return Math.ceil(this.filteredArticleList.length / this.articlesPerPage);
	}

	@action.bound
	private translateBlogSections() {
		const { taxonomies, translations } = this.coreStore;

			const tax = taxonomies.get(KENTICO_TAXONOMY.BLOG_SECTIONS);
			if (!tax) {
				return;
			}
			tax.forEach((sectionTaxonomy) => {
			const sectionTranslation = translations && Array.isArray(translations.blogSections)
				&& translations.blogSections.find(
				(st) => st.blogSectionCodeName === sectionTaxonomy.codename);
			const translation = sectionTranslation ? sectionTranslation.translation : sectionTaxonomy.name;
			this.blogSections.set(sectionTaxonomy.codename, translation);
		});

	}

	@action.bound
	public setPage(pageNumber: number) {
		this.page = pageNumber;
	}

	@action.bound
	setFilter(...filter: string[]) {
		if (this.filter !== filter) {
			this.filter = filter;
			this.setPage(1);
		}
	}

	@action.bound
	clearFilter() {
		this.filter = [];
	}

	@action.bound
	async getArticleList() {
		if (!this.articleListLoaded) {
			try {
				this.articleList = await BlogService.fetchArticleList();
				this.articleListLoaded = true;
			} catch (e) {
				console.error(`Failed to fetch article list: `, e);
			}
		}
	}

	@action.bound
	async getArticle(slug: string) {
		if (!slug) {
			return;
		}
		let article = this.loadedArticles.get(slug);

		if (!article) {
			this.setArticleLoading(true);
			try {
				article = await BlogService.fetchArticle(slug);
				if (article) {
					this.loadedArticles.set(slug, article);
				}
			} catch (e) {
				console.error(`Failed to fetch the article by slug ${slug}: `, e);
			}
			this.setArticleLoading(false);
		}

		if (article) {
			this.coreStore.setContainerFound(true);
			this.currentArticle = article;
		} else {
			this.coreStore.setContainerFound(false);
		}

	}

	validateCategory(category: string) {
		if (this.blogSections.has(slashesToDashes(category)) || category === '') {
			this.coreStore.setContainerFound(true);
		} else {
			this.coreStore.setContainerFound(false);
			this.coreStore.viewStore.currentView = null;
		}
	}

	@action.bound
	setArticleLoading(isArticleLoading: boolean) {
		this.isArticleLoading = isArticleLoading;
	}

}

export default BlogStore;
